import React, { FormEvent, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Typography,
  Grid,
  Box,
  Avatar,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
// import { studentData } from "../../common/types";
import { useForm, Controller } from "react-hook-form";
import { MyCustomButton } from "../../common/MyCustomControls";
import ControlledTextField from "../../common/ControlledComponents/ControlledTextField";
import ControlledSelect from "../../common/ControlledComponents/ControlledSelect";
import PersonIcon from "@mui/icons-material/Person";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { GeneratePrevieUrl } from "../../common/utils/generatePreviewUrl";
// import moment from "moment";
import { v4 as uuid } from "uuid";
// import { sections } from "../../Config/sections_records";
// import { classes_records } from "../../Config/classes_records";
// import { vehicles_records } from "../../Config/vehicles_records";
// import { stops_records } from "../../Config/stops_records";
// import { AddBoxSharp } from "@mui/icons-material";
// import { GetAcademicsRecord } from "../../api/Students-Management/add-view-students";
// import { GetSections } from "../../api/Students-Management/manage-section";
import { Get as GetClass } from "../../api/Control-Settings/manage-class";
// import {
//   GetStops,
//   GetVehicles,
// } from "../../api/Control-Settings/manage-transport";
import { GetCurrentAcademciSession } from "../../helper/helper";
import { useReactToPrint } from "react-to-print";
import AdmissionForm from "../../templates/admissionForm";

interface ProfileDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  profileData: any;
  resetFormRef?: React.MutableRefObject<() => void>;
  isEditing: boolean;
  onEdit: () => void;
  addSibling?: boolean;
  isLoading?: boolean;
}

const ALLOWED_EXTENTIONS = ["jpg", "jpeg", "png"];
const ALLOWED_FILE_SIZE = 2;

const NewAdmissionFormDialog: React.FC<ProfileDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
  profileData,
  resetFormRef,
  isEditing,
  onEdit,
  addSibling,
}) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
    trigger,
    reset,
  } = useForm({
    defaultValues: profileData,
    mode: "onTouched",
    shouldUnregister: false, // Prevents unnecessary re-renders
  });
  const [photo, setPhoto] = useState<string | ArrayBuffer | null>(null);
  const [photofile, setPhotoFile] = useState<File>();
  const [_profileData, _SetProfileData] = useState(profileData);
  const [isFormReady, setIsFormReady] = useState(false);
  const [classList, setClassList] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsgPhoto, setErrorMsgPhoto] = useState("");
  const [sameAddress, setSameAddress] = useState(false);
  // let printRef = useRef();

  const correspondenceAddress = watch(
    "personal_details.correspondence_address"
  );

  // const handlePrint = useReactToPrint({ documentTitle: "" }) as (
  //   content: () => HTMLElement | null
  // ) => () => void;

  useEffect(() => {
    if (sameAddress) {
      // Copy all correspondence address fields to permanent
      setValue(
        "personal_details.permanent_address.addressline1",
        correspondenceAddress.addressline1
      );
      setValue(
        "personal_details.permanent_address.addressline2",
        correspondenceAddress.addressline2
      );
      setValue(
        "personal_details.permanent_address.city",
        correspondenceAddress.city
      );
      setValue(
        "personal_details.permanent_address.state",
        correspondenceAddress.state
      );
      setValue(
        "personal_details.permanent_address.pincode",
        correspondenceAddress.pincode
      );
      // Repeat for addressline2, city, state, etc., if needed
    }
  }, [sameAddress, correspondenceAddress, setValue]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [classes_records] = await Promise.all([GetClass()]);

        if (classes_records && classes_records.result.documents?.length > 0) {
          setClassList(classes_records.result.documents);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (profileData && Object.keys(profileData).length > 0) {
      _SetProfileData(profileData);
    }
  }, [profileData]);

  useEffect(() => {
    if (isOpen && profileData && Object.keys(profileData).length > 0) {
      setIsFormReady(false);
      setTimeout(() => {
        setIsFormReady(true);
      }, 200); // Reduce delay to avoid lag
    }
  }, [isOpen, profileData]); // Remove `_profileData`, rely on `profileData`

  useEffect(() => {
    if (profileData && Object.keys(profileData).length > 0) {
      reset(profileData); // Reset form with new profileData

      const getPhotoUrl = profileData.photoUrl
        ? GeneratePrevieUrl(profileData.photoUrl)
        : "";

      if (profileData.photoUrl) {
        setPhotoFile(undefined);
      }

      setPhoto(getPhotoUrl);
    }
  }, [profileData, reset]); // Ensure it runs only when `profileData` is valid

  useEffect(() => {
    if (resetFormRef) {
      const getPhotoUrl = profileData.photoUrl
        ? GeneratePrevieUrl(profileData.photoUrl)
        : "";
      profileData.photoUrl && setPhotoFile(undefined);
      setPhoto(getPhotoUrl);
      resetFormRef.current = () => reset(profileData);
    }
  }, [resetFormRef, reset, profileData]);

  // Create a custom onClose handler for the Dialog component.
  const handleDialogClose = (event: object, reason: string) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      // Prevent closing when clicking outside or pressing escape.
      return;
    }
    // Otherwise, call the parent's onClose function.
    onClose();
  };

  const handleFormSubmit = async (data: any) => {
    try {
      console.log("handleFormSubmit");
      console.log(data);
      const _class = classList.find(
        (item: any) => item.class_id === data.class_id
      );

      const _id = uuid();
      const newApplication = addSibling
        ? {
            ...data,
            admission_class: _class?.name,
            id: _id,
            student_id: _id,
            photofile,
            class_name: _class?.name,
            application_status: "",
            admission_fee: "Pending",
          }
        : {
            ...data,
            photofile,
            class_name: _class?.name,
          };

      console.log("Updated application form");
      console.log(newApplication);

      const isValid = await trigger();
      if (isValid) {
        onSubmit(newApplication);
        onClose();
      } else {
        console.log("Data submission. Validation failed");
      }
    } catch (error) {
      console.log("Error While submitting admission form");
      console.log(error);
    } finally {
    }
  };

  // Handle photo upload
  const handlePhotoUpload = (event: any) => {
    setErrorMsgPhoto("");
    const file = event.target.files[0];
    if (!file) return;

    const maxFileSize = ALLOWED_FILE_SIZE * 1024 * 1024; // 2MB

    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    const fileSize = file.size;
    console.log("fileExtension", fileExtension);
    console.log("fileSize", fileSize);

    // Validate file extension
    if (!fileExtension || !ALLOWED_EXTENTIONS.includes(fileExtension)) {
      setErrorMsgPhoto("Invalid file type.");
      return;
    }

    // Validate file size
    if (fileSize > maxFileSize) {
      setErrorMsgPhoto("File size exceeds 2MB");
      return;
    }

    console.log("Photo Upload");
    console.log(event);
    setPhotoFile(file);
    // formData.append("file", file);
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setPhoto(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // const handlePrintApplication = async () => {
  //   // try {
  //   //   const element = printRef.current;
  //   //   // if (element) {
  //   //   //   //@ts-ignore
  //   //   //   element.classList.add("print-landscape"); // or "print-portrait"
  //   //   // }

  //   //   handlePrint(() => element!)();
  //   // } catch (error) {
  //   //   console.log("Handling print function call error.");
  //   //   console.log(error);
  //   // }

  //   try {
  //     handlePrint(() => printRef.current!)();
  //   } catch (error) {
  //     console.log("Handling print function call error.");
  //     console.log(error);
  //   }
  // };

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={handleDialogClose}
        fullWidth
        maxWidth={false}
        sx={{
          display: "flex",
          alignItems: "center", // ✅ Center vertically
          justifyContent: "center", // ✅ Center horizontally
          "& .MuiDialog-paper": {
            width: "70vw", // Set dialog width
            height: "100vh",
            maxWidth: "90vw",
            minHeight: "70vh", // Prevents it from being too short
          },
        }}
      >
        <DialogTitle>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            // height: "90vh",
            // overflowX: "auto",
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: "8px", // Width of the scrollbar
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1", // Background of the scrollbar track
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888", // Color of the scroll thumb
              borderRadius: "10px", // Rounded corners
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#555", // Darker color on hover for the thumb
            },
          }}
          // sx={{
          //   width: "60vw",
          //   height: "80vh", // Ensure enough height
          //   overflow: "auto",
          //   "& .MuiDataGrid-root": {
          //     minWidth: "100%", // Ensure DataGrid takes full width
          //   },
          // }}
        >
          {!isFormReady || !profileData || loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="500px"
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                p={1}
                width="98%"
              >
                <Box
                  width="100%"
                  bgcolor="grey.100"
                  // border={2}
                  // borderColor="black"
                  p={2}
                >
                  <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Grid item>
                      <img
                        src="/img/vpm_gns_logo.png"
                        width={140}
                        height={100}
                        alt="School Logo"
                      />
                    </Grid>
                    <Grid item xs textAlign="center" mr={8}>
                      <Typography
                        variant="h4"
                        fontWeight="bold"
                        textTransform="uppercase"
                        sx={{ fontFamily: "Alien Invader" }}
                      >
                        A.B.C Eduern Learning School
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        A sr.secondary (10+2) school, Affiliated to C.B.S.E,
                        Delhi
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        Affiliation no. 3435342, School Code - 66XX6, U-Dise no.
                        2002010XX53
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
                <Box
                  width="40%"
                  border={1.6}
                  borderColor="black"
                  mt={2}
                  p={1}
                  textAlign={"center"}
                >
                  <Typography variant="h6" fontWeight="bold">
                    {`ADMISSION FORM ${GetCurrentAcademciSession()}`}
                  </Typography>
                </Box>
              </Box>

              <form onSubmit={handleSubmit(handleFormSubmit)}>
                {/* Admission Details */}
                <Accordion
                  sx={{
                    mt: 2,
                    borderRadius: 1,
                    // boxShadow: "none",
                    // border: "none",
                    // "&::before": {
                    //   display: "none", // removes the default divider line above AccordionSummary
                    // },
                  }}
                  defaultExpanded
                >
                  <AccordionSummary>
                    <Typography variant="h6">
                      <strong>Admission Details</strong>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={4} sx={{ mt: -3 }}>
                      <Grid
                        item
                        xs={12}
                        display={"flex"}
                        flexDirection={"column"}
                        sx={{ mt: -3 }}
                      >
                        <Box
                          display={"flex"}
                          flexDirection={"row"}
                          justifyContent={"flex-start"}
                          alignItems={"center"}
                          gap={1}
                        >
                          <Typography variant="body2" pt={0}>
                            <strong>Admission Date: </strong>
                          </Typography>
                          <ControlledTextField
                            name="admission_date"
                            control={control}
                            errors={errors}
                            // label="Admission Date"
                            fullWidth
                            type="date"
                            disabled={addSibling ? false : !isEditing}
                            sx={{
                              width: "15%",
                              mt: 0,
                              ml: 0,
                              "& .MuiInputBase-input.Mui-disabled": {
                                WebkitTextFillColor: "#000000",
                              },
                            }}
                          />
                        </Box>
                        <Box
                          display={"flex"}
                          flexDirection={"row"}
                          justifyContent={"flex-start"}
                          alignItems={"center"}
                          gap={1}
                          mt={2}
                        >
                          <Typography variant="body2" pt={0}>
                            <strong>Class to which admission sought: </strong>
                          </Typography>
                          <ControlledSelect
                            name={`class_id`}
                            control={control}
                            errors={errors}
                            label="Class"
                            rules={{ required: "Required" }}
                            options={classList.map((item: any) => ({
                              value: item.class_id,
                              label: item.name,
                            }))}
                            sx={{
                              width: "20%",
                              mt: -1,
                              "& .MuiInputBase-input.Mui-disabled": {
                                WebkitTextFillColor: "#000000",
                              },
                            }}
                            disabled={addSibling ? false : !isEditing}
                            required
                          />
                        </Box>
                        <ControlledTextField
                          name="admission_id"
                          control={control}
                          errors={errors}
                          label="Admission Id"
                          fullWidth
                          disabled={addSibling ? false : !isEditing}
                          sx={{
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: "#000000",
                            },
                          }}
                        />

                        {/* </Grid> */}
                        <ControlledTextField
                          name="apaar"
                          control={control}
                          errors={errors}
                          label="Apaar Id"
                          fullWidth
                          disabled={!isEditing}
                          sx={{
                            mt: 1,
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: "#000000",
                            },
                          }}
                        />

                        <ControlledTextField
                          name="pen"
                          control={control}
                          errors={errors}
                          label="Pen Id"
                          sx={{
                            mt: 1,
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: "#000000",
                            },
                          }}
                          fullWidth
                          disabled={!isEditing}
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        display={"flex"}
                        flexDirection={"row"}
                        justifyContent={"normal"}
                        sx={{
                          mt: -1,
                          "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: "#000000",
                          },
                        }}
                        gap={2}
                      >
                        <ControlledSelect
                          name="admission_catagory"
                          control={control}
                          errors={errors}
                          label="Ward Type"
                          // rules={{ required: "Required" }}
                          options={[
                            { value: "general", label: "Old" },
                            { value: "new", label: "New" },
                            { value: "bpl", label: "BPL" },
                            { value: "staff_ward", label: "Staff Ward" },
                          ]}
                          sx={{
                            width: "31%",
                            mt: 0,
                            ml: 0,
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: "#000000",
                            },
                          }}
                          disabled={true}
                        />

                        <ControlledSelect
                          name="admission_scheme"
                          control={control}
                          errors={errors}
                          label="Scheme"
                          // rules={{ required: "Required" }}
                          options={[
                            { value: "RTE", label: "RTE" },
                            { value: "Non RTE", label: "Non RTE" },
                          ]}
                          sx={{
                            width: "31%",
                            mt: 0,
                            ml: 0,
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: "#000000",
                            },
                          }}
                          disabled={!isEditing}
                        />
                        <ControlledSelect
                          name="cast"
                          control={control}
                          errors={errors}
                          label="Cast"
                          // rules={{ required: "Required" }}
                          options={[
                            { value: "GEN", label: "GEN" },
                            { value: "OBC", label: "OBC" },
                            { value: "ST", label: "ST" },
                            { value: "SC", label: "SC" },
                          ]}
                          sx={{
                            width: "31%",
                            mt: 0,
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: "#000000",
                            },
                          }}
                          disabled={!isEditing}
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        display={"flex"}
                        flexDirection={"row"}
                        justifyContent={"normal"}
                        sx={{
                          mt: -1,
                          "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: "#000000",
                          },
                        }}
                        gap={2}
                      >
                        <ControlledSelect
                          name="girl_single_child"
                          control={control}
                          errors={errors}
                          label="Single Girl Child"
                          // rules={{ required: "Required" }}
                          options={[
                            { value: "YES", label: "YES" },
                            { value: "NO", label: "NO" },
                          ]}
                          sx={{
                            width: "31%",
                            mt: 0,
                            ml: 0,
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: "#000000",
                            },
                          }}
                          disabled={!isEditing}
                        />

                        <ControlledSelect
                          name="physically_disabled"
                          control={control}
                          errors={errors}
                          label="Is Physically Disabled"
                          // rules={{ required: "Required" }}
                          options={[
                            { value: "YES", label: "YES" },
                            { value: "NO", label: "NO" },
                          ]}
                          sx={{
                            width: "31%",
                            mt: 0,
                            ml: 0,
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: "#000000",
                            },
                          }}
                          disabled={!isEditing}
                        />
                        <ControlledSelect
                          name="is_ews"
                          control={control}
                          errors={errors}
                          label="Belong to EWS"
                          // rules={{ required: "Required" }}
                          options={[
                            { value: "YES", label: "YES" },
                            { value: "NO", label: "NO" },
                          ]}
                          sx={{
                            width: "31%",
                            mt: 0,
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: "#000000",
                            },
                          }}
                          disabled={!isEditing}
                        />
                      </Grid>
                      <Typography variant="body2" pl={2} pt={2} ml={2}>
                        <strong>Previous School Details</strong>
                      </Typography>
                      {/* Previous School Details */}
                      <Grid
                        item
                        xs={12}
                        display={"flex"}
                        flexDirection={"row"}
                        justifyContent={"flex-start"}
                        gap={2}
                      >
                        <ControlledTextField
                          name="previous_school.name"
                          control={control}
                          errors={errors}
                          label="School Name"
                          sx={{
                            width: "40%",
                            mt: 0,
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: "#000000",
                            },
                          }}
                          disabled={!isEditing}
                        />
                        <ControlledTextField
                          name="previous_school.last_class_attended"
                          control={control}
                          errors={errors}
                          label="Last Class Attended"
                          sx={{
                            width: "30%",
                            mt: 0,
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: "#000000",
                            },
                          }}
                          disabled={!isEditing}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                {/* Personal Details */}
                <Accordion sx={{ mt: 1, borderRadius: 1 }} defaultExpanded>
                  <AccordionSummary>
                    <Typography variant="h6">
                      <strong>Personal Details</strong>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={4} sx={{ mt: -3 }}>
                      <Grid
                        item
                        xs={12}
                        display="flex"
                        flexDirection={"row"}
                        justifyContent={"normal"}
                        gap={3}
                      >
                        <Grid
                          item
                          width={"15%"}
                          display={"flex"}
                          flexDirection={"column"}
                        >
                          <Box
                            sx={{
                              mt: { xs: 2, md: 0 },
                              width: 132,
                              height: 170,
                              border: "2px solid #ccc",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#f5f5f5",
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                position: "relative",
                                width: "100%",
                                height: "100%",
                              }}
                            >
                              {photo ? (
                                <>
                                  <Avatar
                                    src={photo as string}
                                    alt="Student Photo"
                                    sx={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "contain",
                                    }}
                                    variant="square"
                                  />
                                  {/* Hover effect for Camera Icon */}
                                  {(addSibling || isEditing) && (
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        top: 0,
                                        right: 0,
                                        bottom: 0,
                                        left: 0,
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        bgcolor: "rgba(0, 0, 0, 0.4)", // Dark overlay on hover
                                        opacity: 0,
                                        transition: "opacity 0.3s ease",
                                        cursor: "pointer",
                                        "&:hover": {
                                          opacity: 1, // Show icon on hover
                                        },
                                      }}
                                      onClick={() =>
                                        document
                                          .getElementById("photo-upload")
                                          ?.click()
                                      } // Trigger file input on click
                                    >
                                      <Box
                                        display={"flex"}
                                        flexDirection="column"
                                        justifyContent={"center"}
                                        alignItems={"center"}
                                      >
                                        <PhotoCameraIcon
                                          sx={{
                                            color: "white",
                                            fontSize: 40,
                                          }}
                                        />
                                        <Typography
                                          variant="caption"
                                          alignSelf={"center"}
                                          color="#fff"
                                          sx={{ fontSize: "0.65rem" }}
                                        >
                                          <strong>jpg, jpeg, png</strong> max{" "}
                                          <strong>2MB</strong>
                                        </Typography>
                                      </Box>
                                    </Box>
                                  )}
                                  <input
                                    id="photo-upload"
                                    type="file"
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    onChange={handlePhotoUpload}
                                  />
                                </>
                              ) : (
                                <>
                                  <Avatar
                                    sx={{
                                      width: "100%",
                                      height: "100%",
                                      bgcolor: "#f0f0f0", // Background color for the empty avatar
                                    }}
                                    variant="square"
                                  >
                                    <PersonIcon
                                      sx={{ fontSize: 120, color: "#bdbdbd" }}
                                    />
                                  </Avatar>
                                  {/* Hover effect for Camera Icon when no photo */}
                                  {(addSibling || isEditing) && (
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        top: 0,
                                        right: 0,
                                        bottom: 0,
                                        left: 0,
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        bgcolor: "rgba(0, 0, 0, 0.4)", // Dark overlay on hover
                                        opacity: 0,
                                        transition: "opacity 0.3s ease",
                                        cursor: "pointer",
                                        "&:hover": {
                                          opacity: 1, // Show icon on hover
                                        },
                                      }}
                                      onClick={() =>
                                        document
                                          .getElementById("photo-upload")
                                          ?.click()
                                      } // Trigger file input on click
                                    >
                                      <Box
                                        display={"flex"}
                                        flexDirection="column"
                                        justifyContent={"center"}
                                        alignItems={"center"}
                                      >
                                        <PhotoCameraIcon
                                          sx={{
                                            color: "white",
                                            fontSize: 40,
                                          }}
                                        />
                                        <Typography
                                          variant="caption"
                                          alignSelf={"center"}
                                          color="#fff"
                                          sx={{ fontSize: "0.65rem" }}
                                        >
                                          <strong>jpg, jpeg, png</strong> max{" "}
                                          <strong>2MB</strong>
                                        </Typography>
                                      </Box>
                                    </Box>
                                  )}

                                  <input
                                    id="photo-upload"
                                    type="file"
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    onChange={handlePhotoUpload}
                                  />
                                </>
                              )}
                            </Box>
                          </Box>
                          {errorMsgPhoto && (
                            <Typography
                              variant="caption"
                              alignSelf={"center"}
                              color="red"
                              sx={{ fontSize: "0.65rem" }}
                            >
                              {errorMsgPhoto}
                            </Typography>
                          )}
                        </Grid>

                        <Grid
                          container
                          width={"80%"}
                          display="flex"
                          flexDirection={"column"}
                          justifyContent={"flex-start"}
                        >
                          {/* Full Name */}
                          <Grid item width={"100%"} mt={0}>
                            <ControlledTextField
                              name="personal_details.name"
                              control={control}
                              errors={errors}
                              label="Student's Full Name"
                              rules={{
                                required: "Required",
                              }}
                              // sx={{ width: "100%" }}
                              fullWidth
                              required
                              disabled={addSibling ? false : !isEditing}
                            />
                          </Grid>

                          {/* DOB */}
                          <Grid
                            item
                            width={"100%"}
                            display={"flex"}
                            flexDirection={"row"}
                            // justifyContent={"normal"}
                            justifyContent={"space-between"}
                            mt={2}
                            // sx={{ mt: -1 }}
                          >
                            <Box
                              display={"flex"}
                              flexDirection={"row"}
                              justifyContent={"flex-start"}
                              alignItems={"center"}
                              gap={1}
                              mt={1}
                            >
                              <Typography variant="body2" pt={0}>
                                <strong>Date of Birth: </strong>
                              </Typography>
                              <ControlledTextField
                                name="personal_details.dob"
                                control={control}
                                errors={errors}
                                // label="Admission Date"
                                // fullWidth
                                type="date"
                                disabled={addSibling ? false : !isEditing}
                                sx={{ width: "50%" }}
                              />
                            </Box>
                          </Grid>

                          <Grid
                            item
                            width={"100%"}
                            display={"flex"}
                            flexDirection={"row"}
                            justifyContent={"space-between"}
                            mt={3}
                            gap={2}
                            // sx={{ mt: -1 }}
                          >
                            <ControlledSelect
                              name="personal_details.gender"
                              control={control}
                              errors={errors}
                              label="Gender"
                              options={[
                                { value: "", label: "Select" },
                                { value: "male", label: "male" },
                                { value: "female", label: "female" },
                                { value: "other", label: "other" },
                              ]}
                              sx={{ width: "31%", mt: 0, pb: 0 }}
                              disabled={addSibling ? false : !isEditing}
                            />

                            <ControlledTextField
                              name="adhaar"
                              control={control}
                              errors={errors}
                              label="Adhaar No"
                              sx={{ mt: 0 }}
                              fullWidth
                              disabled={!isEditing}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      <Typography variant="body2" pl={2} pt={2} ml={2}>
                        <strong>Residential Address</strong>
                      </Typography>

                      {/*Residential Address */}
                      <Grid
                        item
                        xs={12}
                        display={"flex"}
                        flexDirection={"column"}
                        sx={{ mt: -3 }}
                      >
                        <ControlledTextField
                          name="personal_details.correspondence_address.addressline1"
                          control={control}
                          errors={errors}
                          label="Address Line1"
                          // value={formData?.studentObj.personalDetails.addressline1}
                          rules={{
                            required: "Required",
                          }}
                          fullWidth
                          required
                          disabled={!isEditing}
                        />

                        <ControlledTextField
                          name="personal_details.correspondence_address.addressline2"
                          control={control}
                          errors={errors}
                          label="Address Line2"
                          // value={formData?.studentObj.personalDetails.addressline2}
                          sx={{ mt: 1 }}
                          fullWidth
                          disabled={!isEditing}
                        />

                        <Grid
                          display={"flex"}
                          flexDirection={"row"}
                          sx={{ mt: 1 }}
                        >
                          <ControlledTextField
                            name="personal_details.correspondence_address.city"
                            control={control}
                            errors={errors}
                            label="City"
                            // value={formData?.studentObj.personalDetails.addresscity}
                            rules={{
                              required: "Required",
                            }}
                            fullWidth
                            required
                            sx={{ mr: 2 }}
                            disabled={!isEditing}
                          />

                          <ControlledTextField
                            name="personal_details.correspondence_address.state"
                            control={control}
                            errors={errors}
                            label="State"
                            // value={formData?.studentObj.personalDetails.addressstate}
                            rules={{
                              required: "Required",
                            }}
                            fullWidth
                            required
                            sx={{ ml: 2, mr: 2 }}
                            disabled={!isEditing}
                          />

                          <ControlledTextField
                            name="personal_details.correspondence_address.pincode"
                            control={control}
                            errors={errors}
                            label="Pincode"
                            rules={{
                              required: "Required",
                            }}
                            fullWidth
                            required
                            sx={{ ml: 2 }}
                            disabled={!isEditing}
                          />
                        </Grid>
                      </Grid>

                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={sameAddress}
                              onChange={(e: any) =>
                                setSameAddress(e.target.checked)
                              }
                            />
                          }
                          label="Permanent address is same as Residential address"
                        />
                      </Grid>

                      <Typography variant="body2" pl={2} pt={2} ml={2}>
                        <strong>Permanent Address</strong>
                      </Typography>

                      {/*Permanent Address */}
                      <Grid
                        item
                        xs={12}
                        display={"flex"}
                        flexDirection={"column"}
                        sx={{ mt: -3 }}
                      >
                        <ControlledTextField
                          name="personal_details.permanent_address.addressline1"
                          control={control}
                          errors={errors}
                          label="Address Line1"
                          // value={formData?.studentObj.personalDetails.addressline1}
                          rules={{
                            required: "Required",
                          }}
                          fullWidth
                          required
                          disabled={sameAddress ? true : !isEditing}
                        />

                        <ControlledTextField
                          name="personal_details.permanent_address.addressline2"
                          control={control}
                          errors={errors}
                          label="Address Line2"
                          // value={formData?.studentObj.personalDetails.addressline2}
                          sx={{ mt: 1 }}
                          fullWidth
                          disabled={sameAddress ? true : !isEditing}
                        />

                        <Grid
                          display={"flex"}
                          flexDirection={"row"}
                          sx={{ mt: 1 }}
                        >
                          <ControlledTextField
                            name="personal_details.permanent_address.city"
                            control={control}
                            errors={errors}
                            label="City"
                            // value={formData?.studentObj.personalDetails.addresscity}
                            rules={{
                              required: "Required",
                            }}
                            fullWidth
                            required
                            sx={{ mr: 2 }}
                            disabled={sameAddress ? true : !isEditing}
                          />

                          <ControlledTextField
                            name="personal_details.permanent_address.state"
                            control={control}
                            errors={errors}
                            label="State"
                            // value={formData?.studentObj.personalDetails.addressstate}
                            rules={{
                              required: "Required",
                            }}
                            fullWidth
                            required
                            sx={{ ml: 2, mr: 2 }}
                            disabled={sameAddress ? true : !isEditing}
                          />

                          <ControlledTextField
                            name="personal_details.permanent_address.pincode"
                            control={control}
                            errors={errors}
                            label="Pincode"
                            rules={{
                              required: "Required",
                            }}
                            fullWidth
                            required
                            sx={{ ml: 2 }}
                            disabled={sameAddress ? true : !isEditing}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                {/* Parents Details */}
                <Accordion sx={{ mt: 1, borderRadius: 1 }} defaultExpanded>
                  <AccordionSummary>
                    <Typography variant="h6">
                      <strong>Parents Details</strong>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" pt={2}>
                      <strong>Father's Details</strong>
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                      <Grid item xs={12}>
                        <ControlledTextField
                          name="father_details.name"
                          control={control}
                          errors={errors}
                          label="Name"
                          rules={{
                            required: "Required",
                          }}
                          fullWidth
                          required
                          sx={{ width: "65%" }}
                          disabled={!isEditing}
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        display={"flex"}
                        flexDirection={"row"}
                        justifyContent={"space-between"}
                      >
                        <ControlledSelect
                          name="father_details.education"
                          control={control}
                          errors={errors}
                          label="Education"
                          // rules={{ required: "Required" }}
                          options={[
                            { value: "", label: "Select" },
                            { value: "Graduate", label: "Graduate" },
                            { value: "PG", label: "PG" },
                            { value: "Masters", label: "Masters" },
                            { value: "Others", label: "Others" },
                          ]}
                          sx={{ width: "40%", mt: 0 }}
                          disabled={!isEditing}
                        />

                        <ControlledSelect
                          name="father_details.occupation"
                          control={control}
                          errors={errors}
                          label="Occupation"
                          // rules={{ required: "Required" }}
                          options={[
                            { value: "", label: "Select" },
                            { value: "Business", label: "Business" },
                            { value: "Service", label: "Service" },
                            { value: "Others", label: "Others" },
                          ]}
                          sx={{ width: "40%", mt: 0 }}
                          disabled={!isEditing}
                        />
                      </Grid>

                      {/**Phone Number and Adhaar*/}
                      <Grid
                        item
                        xs={12}
                        display={"flex"}
                        flexDirection={"row"}
                        justifyContent={"space-between"}
                        gap={3}
                      >
                        <ControlledTextField
                          name="father_details.contact"
                          control={control}
                          errors={errors}
                          label="Phone"
                          sx={{ width: "40%", mt: 0 }}
                          rules={{
                            // required: "Required",
                            pattern: {
                              value: /^[0-9]{10}$/,
                              message: "Invalid Number",
                            },
                          }}
                          fullWidth
                          // required
                          disabled={!isEditing}
                        />
                        <ControlledTextField
                          name="father_details.adhaar"
                          control={control}
                          errors={errors}
                          label="Adhaar No"
                          sx={{ width: "40%", mt: 0 }}
                          fullWidth
                          disabled={!isEditing}
                        />
                      </Grid>

                      {/* Annual Income */}
                      <Grid
                        item
                        xs={12}
                        display={"flex"}
                        flexDirection={"row"}
                        justifyContent={"space-between"}
                        gap={3}
                      >
                        <ControlledTextField
                          name="father_details.annual_income"
                          control={control}
                          errors={errors}
                          label="Annual Income"
                          sx={{ width: "40%", mt: 0 }}
                          fullWidth
                          disabled={!isEditing}
                        />
                      </Grid>
                    </Grid>

                    <Typography variant="body2" pt={2} mt={2}>
                      <strong>Mother's Details</strong>
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                      <Grid item xs={12}>
                        <ControlledTextField
                          name="mother_details.name"
                          control={control}
                          errors={errors}
                          label="Name"
                          // rules={{
                          //   required: "Required",
                          // }}
                          fullWidth
                          // required
                          sx={{ width: "65%" }}
                          disabled={!isEditing}
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        display={"flex"}
                        flexDirection={"row"}
                        justifyContent={"space-between"}
                      >
                        <ControlledSelect
                          name="mother_details.education"
                          control={control}
                          errors={errors}
                          label="Education"
                          // rules={{ required: "Required" }}
                          options={[
                            { value: "", label: "Select" },
                            { value: "Graduate", label: "Graduate" },
                            { value: "PG", label: "PG" },
                            { value: "Masters", label: "Masters" },
                            { value: "Others", label: "Others" },
                          ]}
                          sx={{ width: "40%", mt: 0 }}
                          disabled={!isEditing}
                        />

                        <ControlledSelect
                          name="mother_details.occupation"
                          control={control}
                          errors={errors}
                          label="Occupation"
                          // rules={{ required: "Required" }}
                          options={[
                            { value: "", label: "Select" },
                            { value: "Business", label: "Business" },
                            { value: "Service", label: "Service" },
                            { value: "Others", label: "Others" },
                          ]}
                          sx={{ width: "40%", mt: 0 }}
                          disabled={!isEditing}
                        />
                      </Grid>

                      {/**Phone Number and Adhaar*/}
                      <Grid
                        item
                        xs={12}
                        display={"flex"}
                        flexDirection={"row"}
                        justifyContent={"space-between"}
                        gap={3}
                      >
                        <ControlledTextField
                          name="mother_details.contact"
                          control={control}
                          errors={errors}
                          label="Phone"
                          sx={{ width: "40%", mt: 0 }}
                          rules={{
                            // required: "Required",
                            pattern: {
                              value: /^[0-9]{10}$/,
                              message: "Invalid Number",
                            },
                          }}
                          fullWidth
                          // required
                          disabled={!isEditing}
                        />
                        <ControlledTextField
                          name="mother_details.adhaar"
                          control={control}
                          errors={errors}
                          label="Adhaar No"
                          sx={{ width: "40%", mt: 0 }}
                          fullWidth
                          disabled={!isEditing}
                        />
                      </Grid>

                      {/* Annual Income */}
                      <Grid
                        item
                        xs={12}
                        display={"flex"}
                        flexDirection={"row"}
                        justifyContent={"space-between"}
                        gap={3}
                      >
                        <ControlledTextField
                          name="mother_details.annual_income"
                          control={control}
                          errors={errors}
                          label="Annual Income"
                          sx={{ width: "40%", mt: 0 }}
                          fullWidth
                          disabled={!isEditing}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                {/* Guardian Details */}
                <Accordion sx={{ mt: 1, borderRadius: 1 }} defaultExpanded>
                  <AccordionSummary>
                    <Typography variant="h6">
                      <strong>Local Guardian</strong>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                      {/* Father's Name */}
                      <Grid item xs={12}>
                        <ControlledTextField
                          name="guardian_details.name"
                          control={control}
                          errors={errors}
                          label="Name"
                          fullWidth
                          sx={{ width: "65%" }}
                          disabled={!isEditing}
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        display={"flex"}
                        flexDirection={"row"}
                        justifyContent={"space-between"}
                        gap={2}
                      >
                        <ControlledSelect
                          name="guardian_details.education"
                          control={control}
                          errors={errors}
                          label="Education"
                          // rules={{ required: "Required" }}
                          options={[
                            { value: "", label: "Select" },
                            { value: "Graduate", label: "Graduate" },
                            { value: "PG", label: "PG" },
                            { value: "Masters", label: "Masters" },
                            { value: "Others", label: "Others" },
                          ]}
                          sx={{ width: "40%", mt: 0 }}
                          disabled={!isEditing}
                        />

                        {/* Relation with Student */}

                        <ControlledSelect
                          name="guardian_details.relation"
                          control={control}
                          errors={errors}
                          label="Blood Relation"
                          // rules={{ required: "Required" }}
                          options={[
                            { value: "", label: "Select" },
                            { value: "Father", label: "Father" },
                            { value: "Mother", label: "Mother" },
                            {
                              value: "Grand-Children",
                              label: "Grand-Children",
                            },
                          ]}
                          sx={{ width: "40%", mt: 0 }}
                          disabled={!isEditing}
                        />

                        <ControlledSelect
                          name="guardian_details.occupation"
                          control={control}
                          errors={errors}
                          label="Occupation"
                          // rules={{ required: "Required" }}
                          options={[
                            { value: "", label: "Select" },
                            { value: "Business", label: "Business" },
                            { value: "Service", label: "Service" },
                            { value: "Others", label: "Others" },
                          ]}
                          sx={{ width: "40%", mt: 0 }}
                          disabled={!isEditing}
                        />
                      </Grid>

                      {/**Phone Number and Adhaar*/}
                      <Grid
                        item
                        xs={12}
                        display={"flex"}
                        flexDirection={"row"}
                        justifyContent={"space-between"}
                        gap={3}
                      >
                        <ControlledTextField
                          name="guardian_details.contact"
                          control={control}
                          errors={errors}
                          label="Phone"
                          sx={{ width: "40%", mt: 0 }}
                          rules={{
                            // required: "Required",
                            pattern: {
                              value: /^[0-9]{10}$/,
                              message: "Invalid Number",
                            },
                          }}
                          fullWidth
                          // required
                          disabled={!isEditing}
                        />
                        <ControlledTextField
                          name="guardian_details.adhaar"
                          control={control}
                          errors={errors}
                          label="Adhaar No"
                          sx={{ width: "40%", mt: 0 }}
                          fullWidth
                          disabled={!isEditing}
                        />
                      </Grid>

                      {/* Annual Income */}
                      <Grid
                        item
                        xs={12}
                        display={"flex"}
                        flexDirection={"row"}
                        justifyContent={"space-between"}
                        gap={3}
                      >
                        <ControlledTextField
                          name="guardian_details.annual_income"
                          control={control}
                          errors={errors}
                          label="Annual Income"
                          sx={{ width: "40%", mt: 0 }}
                          fullWidth
                          disabled={!isEditing}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                {_profileData.application_status !== "Completed" && (
                  <DialogActions>
                    <MyCustomButton variant="contained" onClick={onClose}>
                      Close
                    </MyCustomButton>

                    {addSibling
                      ? ""
                      : !isEditing && (
                          <MyCustomButton variant="contained" onClick={onEdit}>
                            Edit
                          </MyCustomButton>
                        )}
                    {(isEditing || addSibling) && (
                      <MyCustomButton variant="contained" type="submit">
                        {addSibling ? "Submit" : "Save Changes"}
                      </MyCustomButton>
                    )}
                  </DialogActions>
                )}
              </form>

              {/* <MyCustomButton
                variant="contained"
                onClick={handlePrintApplication}
                sx={{ alignSelf: "flex-end" }}
              >
                Print Application
              </MyCustomButton> */}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NewAdmissionFormDialog;
